import { useState, useEffect, useRef, useCallback } from 'react';
import { apiGet } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search,
  User,
  Calendar,
  Plane,
  FileText,
  Building2,
  AlertTriangle,
  CheckCircle,
  Loader2,
  X,
  DollarSign,
  Shield,
  MapPin,
  BookUser,
  TreePine,
  ArrowLeft,
  Globe2,
  Download,
} from 'lucide-react';
import { exportWorkerTreePdf } from '@/utils/workerTreePdf';

// ============== HELPERS ==============
const formatBase64Image = (b64) => {
  if (!b64) return null;
  if (typeof b64 !== 'string') return null;
  if (b64.startsWith('data:image')) return b64;
  return `data:image/jpeg;base64,${b64}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    let d;
    if (typeof dateStr === 'string' && /^\d{2}\/\d{2}\/\d{4}/.test(dateStr)) {
      const [day, month, rest] = dateStr.split('/');
      const year = rest.split(' ')[0];
      d = new Date(`${year}-${month}-${day}`);
    } else {
      d = new Date(dateStr);
    }
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const STATUS_LABEL = {
  abroad: { en: 'Abroad', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/40' },
  arrived: { en: 'Arrived', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  settled: { en: 'Settled', cls: 'bg-purple-500/20 text-purple-300 border-purple-500/40' },
  runaway: { en: 'Runaway', cls: 'bg-red-500/20 text-red-300 border-red-500/40' },
  pending: { en: 'Pending', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
};

// ============== BRANCH NODE ==============
// A single tree branch (card) that connects to the central worker photo via an SVG line
const BranchNode = ({ icon: Icon, color, title, subtitle, items = [], log = [], delay = 0, side = 'right' }) => {
  const colorMap = {
    blue:    { ring: 'ring-blue-500/40',    bg: 'from-blue-500/15 to-blue-500/5',       icon: 'text-blue-300',    dot: 'bg-blue-400' },
    purple:  { ring: 'ring-purple-500/40',  bg: 'from-purple-500/15 to-purple-500/5',   icon: 'text-purple-300',  dot: 'bg-purple-400' },
    cyan:    { ring: 'ring-cyan-500/40',    bg: 'from-cyan-500/15 to-cyan-500/5',       icon: 'text-cyan-300',    dot: 'bg-cyan-400' },
    emerald: { ring: 'ring-emerald-500/40', bg: 'from-emerald-500/15 to-emerald-500/5', icon: 'text-emerald-300', dot: 'bg-emerald-400' },
    amber:   { ring: 'ring-amber-500/40',   bg: 'from-amber-500/15 to-amber-500/5',     icon: 'text-amber-300',   dot: 'bg-amber-400' },
    red:     { ring: 'ring-red-500/40',     bg: 'from-red-500/15 to-red-500/5',         icon: 'text-red-300',     dot: 'bg-red-400' },
    orange:  { ring: 'ring-orange-500/40',  bg: 'from-orange-500/15 to-orange-500/5',   icon: 'text-orange-300',  dot: 'bg-orange-400' },
    slate:   { ring: 'ring-slate-500/40',   bg: 'from-slate-500/15 to-slate-500/5',     icon: 'text-slate-300',   dot: 'bg-slate-400' },
  };
  const c = colorMap[color] || colorMap.slate;

  return (
    <motion.div
      initial={{ opacity: 0, x: side === 'right' ? 40 : -40, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ duration: 0.5, delay }}
      className={`relative bg-gradient-to-br ${c.bg} backdrop-blur-sm rounded-2xl ring-1 ${c.ring} p-4 shadow-lg`}
      data-testid={`tree-branch-${color}`}
    >
      {/* Connector dot on the side facing the photo */}
      <div className={`absolute ${side === 'right' ? '-left-2' : '-right-2'} top-6 h-3 w-3 rounded-full ${c.dot} shadow-[0_0_12px_rgba(255,255,255,0.4)]`} />

      <div className="flex items-start gap-3 mb-2">
        <div className={`h-9 w-9 rounded-xl bg-slate-900/60 ring-1 ${c.ring} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-4.5 w-4.5 ${c.icon}`} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-white font-semibold text-sm leading-tight">{title}</h4>
          {subtitle && <p className="text-slate-400 text-xs mt-0.5">{subtitle}</p>}
        </div>
      </div>

      {items.length > 0 && (
        <ul className="space-y-1.5 mt-3 pt-2 border-t border-white/5">
          {items.map((it, i) => (
            <li key={i} className="flex items-center justify-between gap-2 text-xs">
              <span className="text-slate-400">{it.label}</span>
              <span className="text-white font-medium text-right truncate">{it.value || '—'}</span>
            </li>
          ))}
        </ul>
      )}

      {log.length > 0 && (
        <div className="mt-3 pt-2 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
            Payment Log · سجل الدفعات
          </div>
          <ul className="space-y-2 max-h-[180px] overflow-y-auto pr-1" data-testid="worker-tree-payment-log">
            {log.map((p, i) => (
              <li key={i} className="rounded-lg bg-slate-900/40 ring-1 ring-white/5 p-2 text-xs">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className={`font-bold ${c.icon}`}>
                    {(p.currency || 'AED') + ' ' + Number(p.amount || 0).toLocaleString()}
                  </span>
                  <span className="text-slate-500 text-[10px] font-mono">{p.date || '—'}</span>
                </div>
                {p.description ? (
                  <p className="text-slate-300 leading-snug break-words" data-testid={`worker-tree-payment-desc-${i}`}>
                    {p.description}
                  </p>
                ) : (
                  <p className="text-slate-500 italic text-[11px]">No description</p>
                )}
                {p.by && (
                  <p className="text-[10px] text-slate-500 mt-0.5">by {p.by}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
};

// ============== WORKER TREE LAYOUT ==============
// Photo in the center, branches radiating to the sides
const WorkerTreeLayout = ({ data, onBack }) => {
  const { worker, visa, supplier, tickets = [] } = data;
  const status = STATUS_LABEL[worker.status] || { en: worker.status, cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40' };

  const photo = formatBase64Image(worker.photoData || worker.workerPhoto);
  const totalCost = worker.totalCost || worker.contractValue || 0;
  const paid = worker.paidAmount || 0;
  const remaining = worker.remainingAmount ?? Math.max(0, totalCost - paid);

  // Build branches
  const leftBranches = [];
  const rightBranches = [];

  // 1. Registration (left)
  leftBranches.push({
    icon: User,
    color: 'blue',
    title: 'Registration',
    subtitle: 'Worker added to the system',
    items: [
      { label: 'Date', value: formatDate(worker.createdAt) },
      { label: 'Nationality', value: worker.nationality },
      { label: 'Passport', value: worker.passportNumber },
    ],
  });

  // 2. Supplier (right)
  if (supplier) {
    rightBranches.push({
      icon: Building2,
      color: 'orange',
      title: supplier.name || 'Supplier',
      subtitle: 'Linked Supplier',
      items: [
        { label: 'Country', value: supplier.country },
        { label: 'Phone', value: supplier.phone },
        { label: 'Contract', value: totalCost ? `AED ${Number(totalCost).toLocaleString()}` : null },
      ],
    });
  }

  // 3. Visa (left)
  if (visa) {
    leftBranches.push({
      icon: FileText,
      color: 'purple',
      title: visa.visa_number ? `Visa ${visa.visa_number}` : 'Visa',
      subtitle: 'Visa Information',
      items: [
        { label: 'Issue', value: formatDate(visa.issue_date) },
        { label: 'Expiry', value: formatDate(visa.expiry_date) },
        { label: 'Sponsor', value: visa.sponsor_name },
        { label: 'Profession', value: visa.profession },
      ],
    });
  }

  // 4. Flight Tickets (right)
  if (tickets && tickets.length > 0) {
    tickets.slice(0, 3).forEach((t) => {
      const firstSeg = t.flights?.[0] || t.segments?.[0] || {};
      const lastSeg = t.flights?.[t.flights.length - 1] || t.segments?.[t.segments?.length - 1] || firstSeg;
      rightBranches.push({
        icon: Plane,
        color: 'cyan',
        title: `${t.airline || ''} ${t.flightNumber || firstSeg.flightNumber || ''}`.trim() || 'Flight Ticket',
        subtitle: t.pnr ? `PNR: ${t.pnr}` : 'Flight Ticket',
        items: [
          { label: 'Departure', value: formatDate(firstSeg.departureTime || t.departureDate) },
          { label: 'Arrival', value: formatDate(lastSeg.arrivalTime || t.arrivalDate) },
          { label: 'Route', value: `${firstSeg.departure || firstSeg.from || '?'} → ${lastSeg.arrival || lastSeg.to || 'DXB'}` },
          { label: 'Status', value: t.flightStatus || t.status || 'scheduled' },
        ],
      });
    });
  }

  // 5. Financials (left)
  const paymentLogEntries = (worker.paymentLog || worker.payments || [])
    .slice()
    .sort((a, b) => (b.timestamp || b.date || '').localeCompare(a.timestamp || a.date || ''));
  leftBranches.push({
    icon: DollarSign,
    color: 'emerald',
    title: 'Financials',
    subtitle: 'Contract & Payments',
    items: [
      { label: 'Total', value: `AED ${Number(totalCost).toLocaleString()}` },
      { label: 'Paid', value: `AED ${Number(paid).toLocaleString()}` },
      { label: 'Remaining', value: `AED ${Number(remaining).toLocaleString()}` },
      { label: 'Payments', value: paymentLogEntries.length },
    ],
    log: paymentLogEntries.map((p) => ({
      amount: p.amount,
      currency: 'AED',
      date: p.date || (p.timestamp ? p.timestamp.slice(0, 10) : ''),
      description: p.description || '',
      by: p.by || '',
    })),
  });

  // 6. Status / Arrival (right)
  if (worker.status === 'arrived' || worker.arrivalDate) {
    rightBranches.push({
      icon: MapPin,
      color: 'emerald',
      title: 'Arrival',
      subtitle: 'Arrived in UAE',
      items: [
        { label: 'Arrived', value: formatDate(worker.arrivalDate) },
        { label: 'Status', value: status.en },
      ],
    });
  }

  // 7. Warranty (left, if any)
  if (worker.warrantyActivated || worker.warrantyExpiryDate) {
    leftBranches.push({
      icon: Shield,
      color: 'amber',
      title: 'Warranty',
      subtitle: 'Coverage Period',
      items: [
        { label: 'Period', value: worker.warrantyPeriod ? `${worker.warrantyPeriod} months` : null },
        { label: 'Activated', value: formatDate(worker.warrantyActivationDate) },
        { label: 'Expires', value: formatDate(worker.warrantyExpiryDate) },
      ],
    });
  }

  // 8. Runaway (right, if any)
  if (worker.status === 'runaway') {
    rightBranches.push({
      icon: AlertTriangle,
      color: 'red',
      title: 'Runaway',
      subtitle: 'Reported missing',
      items: [
        { label: 'Date', value: formatDate(worker.runawayDate) },
        { label: 'Notes', value: worker.runawayNotes },
      ],
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative"
      data-testid="worker-tree-layout"
    >
      {/* Top action row */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-slate-300 hover:text-white hover:bg-white/5"
          data-testid="worker-tree-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Search
        </Button>
        <Button
          onClick={async () => {
            try {
              await exportWorkerTreePdf(data);
              toast.success('PDF تم تصدير الملف · PDF exported successfully');
            } catch (err) {
              console.error('PDF export error:', err);
              toast.error('فشل تصدير PDF · PDF export failed');
            }
          }}
          className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white shadow-lg shadow-orange-500/20"
          data-testid="worker-tree-export-pdf-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          تصدير PDF · Export PDF
        </Button>
      </div>

      {/* Tree Container */}
      <div className="relative bg-gradient-to-b from-slate-950 via-[#0B1220] to-slate-950 rounded-3xl ring-1 ring-white/5 p-4 sm:p-8 overflow-hidden">
        {/* Decorative background grid */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        {/* Desktop Layout: 3 columns */}
        <div className="hidden lg:grid grid-cols-[1fr_auto_1fr] gap-6 items-start relative">
          {/* LEFT BRANCHES */}
          <div className="space-y-5 pt-12">
            {leftBranches.map((b, i) => (
              <div key={`l-${i}`} className="relative">
                {/* Connector line going right (toward photo) */}
                <svg
                  className="absolute top-7 -right-6 w-6 h-0.5 pointer-events-none"
                  viewBox="0 0 24 2"
                  preserveAspectRatio="none"
                >
                  <line x1="0" y1="1" x2="24" y2="1" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="3 3" />
                </svg>
                <BranchNode {...b} delay={i * 0.08} side="right" />
              </div>
            ))}
          </div>

          {/* CENTER: Worker Photo */}
          <div className="flex flex-col items-center sticky top-6">
            <CenterPhotoCard worker={worker} status={status} photo={photo} />
          </div>

          {/* RIGHT BRANCHES */}
          <div className="space-y-5 pt-12">
            {rightBranches.map((b, i) => (
              <div key={`r-${i}`} className="relative">
                <svg
                  className="absolute top-7 -left-6 w-6 h-0.5 pointer-events-none"
                  viewBox="0 0 24 2"
                  preserveAspectRatio="none"
                >
                  <line x1="0" y1="1" x2="24" y2="1" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="3 3" />
                </svg>
                <BranchNode {...b} delay={i * 0.08 + 0.1} side="left" />
              </div>
            ))}
          </div>
        </div>

        {/* Mobile / Tablet Layout: stacked */}
        <div className="lg:hidden flex flex-col items-center gap-5">
          <CenterPhotoCard worker={worker} status={status} photo={photo} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
            {[...leftBranches, ...rightBranches].map((b, i) => (
              <BranchNode key={`m-${i}`} {...b} delay={i * 0.05} side="right" />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============== CENTER PHOTO CARD ==============
const CenterPhotoCard = ({ worker, status, photo }) => (
  <motion.div
    initial={{ scale: 0.85, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.6, type: 'spring' }}
    className="relative"
  >
    {/* Glow */}
    <div className="absolute inset-0 -m-4 bg-gradient-to-br from-orange-500/30 via-amber-500/20 to-transparent blur-2xl rounded-full" />

    <div className="relative bg-gradient-to-br from-slate-900 to-slate-950 rounded-3xl ring-2 ring-orange-500/40 p-5 shadow-2xl shadow-orange-500/10 w-[260px]">
      {/* Photo */}
      <div className="relative mx-auto h-40 w-40 rounded-2xl ring-2 ring-orange-400/60 overflow-hidden bg-slate-800 mb-4">
        {photo ? (
          <img
            src={photo}
            alt={worker.workerName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="h-16 w-16 text-slate-600" strokeWidth={1.2} />
          </div>
        )}
        {/* Status pin */}
        <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold border ${status.cls} backdrop-blur whitespace-nowrap`}>
          {status.en.toUpperCase()}
        </div>
      </div>

      {/* Name */}
      <h2 className="text-white text-center font-bold text-lg leading-snug" data-testid="worker-tree-name">
        {worker.workerName}
      </h2>
      <div className="mt-2 flex flex-col items-center gap-1 text-xs text-slate-400">
        <div className="flex items-center gap-1.5">
          <BookUser className="h-3.5 w-3.5 text-orange-400" />
          <span className="font-mono">{worker.passportNumber || 'No Passport'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Globe2 className="h-3.5 w-3.5 text-blue-400" />
          <span>{worker.nationality || '—'}</span>
        </div>
      </div>
    </div>

    {/* Caption */}
    <p className="text-center text-[10px] text-slate-500 mt-3 tracking-widest uppercase">Worker Tree</p>
  </motion.div>
);

// ============== SEARCH RESULT ROW ==============
const ResultRow = ({ item, onSelect, query }) => {
  const photo = formatBase64Image(item.photo);
  const status = STATUS_LABEL[item.status] || { en: item.status || '—', cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40' };

  // Highlight matches
  const highlight = (text) => {
    if (!text || !query) return text || '—';
    const idx = (text || '').toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="bg-orange-500/30 text-orange-200 rounded px-0.5">{text.slice(idx, idx + query.length)}</span>
        {text.slice(idx + query.length)}
      </>
    );
  };

  return (
    <motion.button
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => onSelect(item)}
      className="w-full text-left bg-slate-900/70 hover:bg-slate-800/70 ring-1 ring-white/5 hover:ring-orange-500/40 rounded-xl p-3 transition-all flex items-center gap-3"
      data-testid={`worker-search-result-${item.id}`}
    >
      <div className="h-12 w-12 rounded-lg overflow-hidden bg-slate-800 ring-1 ring-white/10 flex-shrink-0">
        {photo ? (
          <img src={photo} alt={item.workerName} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="h-6 w-6 text-slate-600" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm font-medium truncate">{highlight(item.workerName)}</div>
        <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
          <span className="font-mono">{highlight(item.passportNumber) || '—'}</span>
          <span>·</span>
          <span>{item.nationality || '—'}</span>
          {item.supplierName && (
            <>
              <span>·</span>
              <span className="truncate flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {item.supplierName}
              </span>
            </>
          )}
        </div>
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${status.cls}`}>
        {status.en}
      </span>
    </motion.button>
  );
};

// ============== MAIN PAGE ==============
const WorkerTreePage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [loadingTree, setLoadingTree] = useState(false);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Live search with debounce
  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setSearching(true);
    try {
      const data = await apiGet(`/api/workers/tree-search?q=${encodeURIComponent(q.trim())}&limit=25`);
      setResults(data?.items || []);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Search error:', err);
        toast.error('Search failed');
      }
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(query);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  // Load tree when worker selected
  const handleSelect = async (item) => {
    setSelectedId(item.id);
    setLoadingTree(true);
    setTreeData(null);
    try {
      const data = await apiGet(`/api/workers/${item.id}/tree`);
      setTreeData(data);
    } catch (err) {
      console.error('Tree load error:', err);
      toast.error('Failed to load tree');
      setSelectedId(null);
    } finally {
      setLoadingTree(false);
    }
  };

  const handleBack = () => {
    setSelectedId(null);
    setTreeData(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0A1424] to-slate-950 p-4 sm:p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex items-center gap-3"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-orange-500/30 rounded-2xl blur-xl" />
          <div className="relative h-12 w-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-700 flex items-center justify-center shadow-lg shadow-orange-500/30">
            <TreePine className="h-6 w-6 text-white" />
          </div>
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Worker Tree</h1>
          <p className="text-slate-400 text-sm">Search and explore a worker's complete history</p>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {!selectedId && (
          <motion.div
            key="search"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Search bar */}
            <Card className="bg-slate-900/70 backdrop-blur-md border-white/5 rounded-2xl mb-6">
              <CardContent className="p-4 sm:p-6">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                  <Input
                    type="text"
                    placeholder="Search by English name or passport number..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-12 pr-12 h-14 text-base bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus-visible:ring-orange-500/40"
                    autoFocus
                    data-testid="worker-tree-search-input"
                  />
                  {query && (
                    <button
                      onClick={() => setQuery('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                      data-testid="worker-tree-clear-btn"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {searching && (
                    <Loader2 className="absolute right-12 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-orange-400" />
                  )}
                </div>

                {/* Hint badges */}
                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                  <span className="px-2 py-1 rounded-md bg-white/5 text-slate-400">e.g. <span className="text-white font-mono">JESSA MAE</span></span>
                  <span className="px-2 py-1 rounded-md bg-white/5 text-slate-400">e.g. <span className="text-white font-mono">A12345678</span></span>
                  <span className="px-2 py-1 rounded-md bg-white/5 text-slate-400">Live search</span>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="space-y-2 max-w-3xl mx-auto">
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <div className="text-center py-16 text-slate-500" data-testid="worker-tree-no-results">
                  <Search className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p>No matching workers</p>
                  <p className="text-xs mt-1 opacity-70">Try different spelling or passport number</p>
                </div>
              )}

              {!query.trim() && (
                <div className="text-center py-20 text-slate-500" data-testid="worker-tree-empty-state">
                  <TreePine className="h-16 w-16 mx-auto mb-4 text-orange-500/40" />
                  <p className="text-base text-slate-300">Start by searching to view the worker tree</p>
                  <p className="text-xs mt-1">Type a name or passport number above</p>
                </div>
              )}

              {results.map((item) => (
                <ResultRow key={item.id} item={item} onSelect={handleSelect} query={query} />
              ))}
            </div>
          </motion.div>
        )}

        {selectedId && (
          <motion.div
            key="tree"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {loadingTree && (
              <div className="flex flex-col items-center justify-center py-32">
                <Loader2 className="h-12 w-12 animate-spin text-orange-500 mb-4" />
                <p className="text-slate-400">Building tree…</p>
              </div>
            )}
            {!loadingTree && treeData && treeData.worker && (
              <WorkerTreeLayout data={treeData} onBack={handleBack} />
            )}
            {!loadingTree && (!treeData || !treeData.worker) && (
              <div className="text-center py-20 text-slate-400">
                <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-amber-400" />
                <p>Failed to load worker data</p>
                <Button onClick={handleBack} variant="ghost" className="mt-4 text-orange-400">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WorkerTreePage;
